import { z } from 'zod'

// Компания — источник истины company-backend. Схемы повторяют wire-контракт
// huma v2 (openapi.json): view-поля обязательны («пустое» = пустая строка),
// реквизиты — вложенный объект requisites. Свободные JSONB-поля — канон hubkit
// map[string]any: contacts/links — {"list": [...]}, bank_details —
// {"raw": "<текст>"}, пустое значение — {}. Доменная модель Company
// разворачивает requisites в плоские поля и объекты — в массивы/строку
// (transform) — UI работает с плоской формой. Теги в view не приходят —
// отдельный API entities/tag.

export const ContactSchema = z.object({
  name: z.string().default(''),
  job_title: z.string().default(''),
  phone: z.string().default(''),
  email: z.string().default(''),
})
export type Contact = z.infer<typeof ContactSchema>

export const LinkSchema = z.object({
  title: z.string().default(''),
  url: z.string().default(''),
})
export type Link = z.infer<typeof LinkSchema>

const ContactListSchema = z
  .object({ list: z.array(ContactSchema).default([]) })
  .transform((o) => o.list)

const LinkListSchema = z.object({ list: z.array(LinkSchema).default([]) }).transform((o) => o.list)

const BankDetailsSchema = z
  .record(z.string(), z.unknown())
  .transform((o) =>
    typeof o.raw === 'string' ? o.raw : Object.keys(o).length === 0 ? '' : JSON.stringify(o),
  )

export const RequisitesSchema = z.object({
  legal_name: z.string(),
  inn: z.string(),
  kpp: z.string(),
  ogrn: z.string(),
  legal_address: z.string(),
  bank_details: BankDetailsSchema,
})
export type Requisites = z.infer<typeof RequisitesSchema>

export const CompanySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    requisites: RequisitesSchema,
    contacts: ContactListSchema.nullable(),
    links: LinkListSchema.nullable(),
    archived: z.boolean(),
    version: z.number(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .transform(({ requisites, contacts, links, ...rest }) => ({
    ...rest,
    ...requisites,
    contacts: contacts ?? [],
    links: links ?? [],
  }))
export type Company = z.output<typeof CompanySchema>

// huma-обёртки ответов: одиночный — {data}; список — {data,total,page,size}.
export const CompanyResponseSchema = z.object({ data: CompanySchema })

export const CompanyListSchema = z.object({
  data: z.array(CompanySchema).nullable(),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

// Тела create/patch (wire CompanyCreateInputBody / CompanyUpdateInputBody):
// реквизиты — вложенным объектом; version обязателен на PATCH (optimistic lock).
// Свободные поля уходят JSONB-объектами канона hubkit; {} — пустое значение.
export interface ContactInput {
  name: string
  job_title?: string
  phone?: string
  email?: string
}

export interface ContactListInput {
  list?: ContactInput[]
}

export interface LinkListInput {
  list?: Link[]
}

export interface BankDetailsInput {
  raw?: string
}

export interface RequisitesInput {
  legal_name?: string
  inn?: string
  kpp?: string
  ogrn?: string
  legal_address?: string
  bank_details?: BankDetailsInput
}

export interface CompanyInput {
  name?: string
  slug?: string
  requisites?: RequisitesInput
  contacts?: ContactListInput
  links?: LinkListInput
  version?: number
}
